'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { I18N_CONF } from '@funkey/locales/config';
import { useI18n } from '~/features/i18n';
import { usePortalCtx } from '~/features/global/portal';
import { captureScrollForModalRestoration } from '~/modules/browser/scroll';
import { HISTORY, HREF } from '~/modules/browser/history';
import type { ActivityCardProps } from './index';

/** Outer element of a PLP activity card, shared by the control and finetuning cards: a bare
 * `<article>` when the card has no link, otherwise an `<article>` around the `Link` that opens
 * the PDP in place, in a new tab or in the modal and records the overview to return to. */
export const ActivityCardFrame: React.FC<{
  href: string;
  disableHref: ActivityCardProps['disableHref'];
  openIn: ActivityCardProps['openIn'];
  className: string;
  children: React.ReactNode;
  onClick?(): void;
  onMouseOver(): void;
  onMouseLeave(): void;
  'data-testid': string;
  'data-sponsored': true | undefined;
}> = t0 => {
  const $ = _c(25);
  let disableHref;
  let href;
  let openIn;
  let wrapperProps;
  if ($[0] !== t0) {
    ({
      href,
      disableHref,
      openIn,
      ...wrapperProps
    } = t0);
    $[0] = t0;
    $[1] = disableHref;
    $[2] = href;
    $[3] = openIn;
    $[4] = wrapperProps;
  } else {
    disableHref = $[1];
    href = $[2];
    openIn = $[3];
    wrapperProps = $[4];
  }
  const i18n = useI18n();
  const portal = usePortalCtx();
  const pathname = usePathname();
  if (disableHref) {
    let t1;
    if ($[5] !== wrapperProps) {
      t1 = <article {...wrapperProps} />;
      $[5] = wrapperProps;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    return t1;
  }
  const shouldOpenInNewTab = openIn === "new-tab";
  const shouldOpenInModal = openIn === "modal";
  let t1;
  if ($[7] !== href) {
    t1 = function getHrefWithReturnUrl() {
      const currentUrl = `${window.location.pathname}${window.location.search}`;
      if (!currentUrl) {
        return href;
      }
      const separator = href.includes("?") ? "&" : "?";
      return `${href}${separator}returnUrl=${encodeURIComponent(currentUrl)}`;
    };
    $[7] = href;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const getHrefWithReturnUrl = t1;
  const t2 = shouldOpenInModal ? false : undefined;
  const t3 = shouldOpenInNewTab ? "_blank" : undefined;
  const t4 = shouldOpenInNewTab ? "noopener noreferrer" : undefined;
  let t5;
  if ($[9] !== getHrefWithReturnUrl || $[10] !== href || $[11] !== i18n.locale || $[12] !== pathname || $[13] !== portal || $[14] !== shouldOpenInModal || $[15] !== shouldOpenInNewTab || $[16] !== wrapperProps) {
    t5 = e => {
      if (shouldOpenInNewTab) {
        e.preventDefault();
        window.open(getHrefWithReturnUrl(), "_blank", "noopener,noreferrer");
      } else {
        if (!shouldOpenInModal) {
          e.preventDefault();
          const activityPath = I18N_CONF.getPageSlugForLocale("activities", i18n.locale);
          const isOnPDP = pathname.includes(`/${activityPath}/`);
          if (!isOnPDP) {
            HISTORY.specialPages.setValue(prev => ({
              ...prev,
              "PDP.backToOverview": {
                ...HREF.parseURL(window.location),
                portal: portal.portal
              }
            }));
          }
          wrapperProps.onClick?.();
          window.location.href = href;
          return;
        }
      }
      captureScrollForModalRestoration(pathname);
      const activityPath_0 = I18N_CONF.getPageSlugForLocale("activities", i18n.locale);
      const isOnPDP_0 = pathname.includes(`/${activityPath_0}/`);
      if (!isOnPDP_0) {
        HISTORY.specialPages.setValue(prev_0 => ({
          ...prev_0,
          "PDP.backToOverview": {
            ...HREF.parseURL(window.location),
            portal: portal.portal
          }
        }));
      }
      wrapperProps.onClick?.();
    };
    $[9] = getHrefWithReturnUrl;
    $[10] = href;
    $[11] = i18n.locale;
    $[12] = pathname;
    $[13] = portal;
    $[14] = shouldOpenInModal;
    $[15] = shouldOpenInNewTab;
    $[16] = wrapperProps;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== href || $[19] !== t2 || $[20] !== t3 || $[21] !== t4 || $[22] !== t5 || $[23] !== wrapperProps) {
    t6 = <article className="contents"><Link {...wrapperProps} href={href} scroll={t2} target={t3} rel={t4} onClick={t5}>{wrapperProps.children}</Link></article>;
    $[18] = href;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = wrapperProps;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  return t6;
};